import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Trash2, Image, Video, Loader2, FolderSync, ExternalLink, ImageOff, Settings, Calendar, CheckCircle, XCircle, Edit, Images, Film } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { es } from 'date-fns/locale';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { galleryApi, eventsApi, settingsApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.08 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } }
};
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';

const GalleryManagerPage = () => {
  const { hasPermission } = useAuth();
  const [gallery, setGallery] = useState([]);
  const [driveGallery, setDriveGallery] = useState([]);
  const [events, setEvents] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDrive, setLoadingDrive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [selectedEventForFolder, setSelectedEventForFolder] = useState(null);
  const [folderIdInput, setFolderIdInput] = useState('');
  const [savingFolder, setSavingFolder] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filterEvent, setFilterEvent] = useState('all');
  const [activeTab, setActiveTab] = useState('manual');
  const [imageErrors, setImageErrors] = useState({});
  const [formData, setFormData] = useState({
    event_id: '',
    media_type: 'image',
    url: '',
    caption: ''
  });

  const fetchData = async () => {
    try {
      const eventFilter = filterEvent !== 'all' ? filterEvent : null;
      const [galleryRes, eventsRes, settingsRes] = await Promise.all([
        galleryApi.getAll(eventFilter),
        eventsApi.getAll(),
        settingsApi.getSite()
      ]);
      setGallery(galleryRes.data);
      setEvents(eventsRes.data);
      setSettings(settingsRes.data);
      
      // Fetch Drive gallery if configured
      if (settingsRes.data?.gallery?.drive_folder_id) {
        setLoadingDrive(true);
        try {
          const driveRes = await galleryApi.getDrive();
          if (!driveRes.data.error) {
            setDriveGallery(driveRes.data.items || []);
          }
        } catch (driveError) {
          console.error('Error fetching Drive gallery:', driveError);
        } finally {
          setLoadingDrive(false);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar galería');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filterEvent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.event_id) {
      toast.error('Selecciona un evento');
      return;
    }

    setSubmitting(true);
    try {
      await galleryApi.add(formData);
      toast.success('Medio agregado');
      setModalOpen(false);
      setFormData({ event_id: '', media_type: 'image', url: '', caption: '' });
      fetchData();
    } catch (error) {
      console.error('Error adding media:', error);
      toast.error('Error al agregar medio');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (mediaId) => {
    if (!confirm('¿Eliminar este medio?')) return;
    
    try {
      await galleryApi.delete(mediaId);
      toast.success('Medio eliminado');
      fetchData();
    } catch (error) {
      console.error('Error deleting media:', error);
      toast.error('Error al eliminar medio');
    }
  };

  const handleImageError = (id) => {
    setImageErrors(prev => ({ ...prev, [id]: true }));
  };

  const handleOpenFolderModal = (event) => {
    setSelectedEventForFolder(event);
    setFolderIdInput(event.drive_folder_id || '');
    setFolderModalOpen(true);
  };

  const handleSaveFolder = async () => {
    if (!selectedEventForFolder) return;
    
    setSavingFolder(true);
    try {
      await eventsApi.update(selectedEventForFolder.id, {
        drive_folder_id: folderIdInput || null
      });
      toast.success('Carpeta de Drive actualizada');
      setFolderModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error updating folder:', error);
      toast.error('Error al actualizar carpeta');
    } finally {
      setSavingFolder(false);
    }
  };

  // Separate events into past and upcoming
  const now = new Date();
  const pastEvents = events.filter(e => new Date(e.date) < now);
  const upcomingEvents = events.filter(e => new Date(e.date) >= now);

  const driveConfigured = settings?.gallery?.drive_folder_id;

  return (
    <SidebarProvider>
      <AdminLayout>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="p-6 lg:p-8 space-y-6"
          data-testid="gallery-manager"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-black uppercase tracking-tight">
                Galería
              </h1>
              <p className="text-zinc-500 text-sm mt-1">
                Gestiona las fotos y videos de tus eventos
              </p>
            </div>
          </motion.div>

          {/* Stats Row */}
          <motion.div variants={itemVariants} className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="stat-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                  <Images size={20} className="text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500">Galería</p>
                  <p className="metric-value-gold text-xl">{driveGallery.length}</p>
                </div>
              </div>
            </div>
            <div className="stat-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <FolderSync size={20} className="text-blue-400" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500">Drive</p>
                  <p className="text-xl font-bold text-blue-400">{driveConfigured ? 'Conectado' : 'N/A'}</p>
                </div>
              </div>
            </div>
            <div className="stat-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <Calendar size={20} className="text-green-400" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500">Eventos</p>
                  <p className="text-xl font-bold text-green-400">{events.length}</p>
                </div>
              </div>
            </div>
            <div className="stat-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <Film size={20} className="text-purple-400" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500">Manual</p>
                  <p className="text-xl font-bold text-purple-400">{gallery.length}</p>
                </div>
              </div>
            </div>
          </motion.div>

            {/* Tabs */}
            <motion.div variants={itemVariants}>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full max-w-md grid-cols-2 bg-[#0A0A0A] border border-[#27272A] rounded-lg p-1 mb-6">
                <TabsTrigger 
                  value="drive" 
                  className="rounded-md data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black font-bold uppercase text-xs tracking-wider"
                >
                  <FolderSync size={14} className="mr-2" />
                  Google Drive
                </TabsTrigger>
                <TabsTrigger 
                  value="manual" 
                  className="rounded-md data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black font-bold uppercase text-xs tracking-wider"
                >
                  <Image size={14} className="mr-2" />
                  Subida Manual
                </TabsTrigger>
              </TabsList>

              {/* Google Drive Tab */}
              <TabsContent value="drive">
                {/* Global Drive Config Card */}
                <div className="admin-card p-6 mb-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${driveConfigured ? 'bg-blue-500/20' : 'bg-[#27272A]'}`}>
                        <FolderSync size={24} className={driveConfigured ? 'text-blue-400' : 'text-[#52525B]'} />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg">Carpeta Global de Drive</h3>
                        <p className="text-zinc-500 text-sm">
                          {driveConfigured 
                            ? `${driveGallery.filter(i => !i.event_id).length} archivos en carpeta global`
                            : 'No configurada'
                          }
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {driveConfigured && (
                        <a 
                          href={`https://drive.google.com/drive/folders/${settings.gallery.drive_folder_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="admin-admin-btn-secondary text-sm flex items-center gap-2"
                        >
                          <ExternalLink size={14} />
                          Abrir
                        </a>
                      )}
                      <Link to="/admin/configuracion" className="admin-admin-btn-secondary text-sm flex items-center gap-2">
                        <Settings size={14} />
                        Configurar
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Events with Drive Folders */}
                <div className="space-y-6">
                  {/* Upcoming Events */}
                  {upcomingEvents.length > 0 && (
                    <div>
                      <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-4 flex items-center gap-2">
                        <Calendar size={14} />
                        Próximos Eventos
                      </h3>
                      <div className="space-y-2">
                        {upcomingEvents.map(event => (
                          <div 
                            key={event.id}
                            className="admin-card p-4 flex items-center justify-between"
                          >
                            <div className="flex items-center gap-4">
                              <div className="w-16 h-12 flex-shrink-0 overflow-hidden rounded-md border border-[#27272A]">
                                <img
                                  src={event.image_url || 'https://images.unsplash.com/photo-1704912343324-a5304d0a160e?w=200'}
                                  alt={event.title}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div>
                                <p className="font-bold">{event.title}</p>
                                <p className="text-xs text-[#52525B]">
                                  {formatInTimeZone(new Date(event.date), 'America/Argentina/Buenos_Aires', 'd MMM yyyy', { locale: es })}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              {event.drive_folder_id ? (
                                <>
                                  <span className="badge-success flex items-center gap-1">
                                    <CheckCircle size={12} />
                                    Drive conectado
                                  </span>
                                  <a 
                                    href={`https://drive.google.com/drive/folders/${event.drive_folder_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="admin-admin-btn-secondary text-xs px-3 py-1.5"
                                  >
                                    <ExternalLink size={12} className="mr-1" />
                                    Abrir
                                  </a>
                                </>
                              ) : (
                                <span className="flex items-center gap-1 text-[#52525B] text-xs">
                                  <XCircle size={14} />
                                  Sin carpeta
                                </span>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenFolderModal(event)}
                                className="text-xs hover:text-[#D4AF37]"
                              >
                                <Edit size={14} className="mr-1" />
                                {event.drive_folder_id ? 'Editar' : 'Agregar'}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Past Events */}
                  {pastEvents.length > 0 && (
                    <div>
                      <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-4 flex items-center gap-2">
                        <Calendar size={14} />
                        Eventos Pasados ({pastEvents.length})
                      </h3>
                      <div className="space-y-2">
                        {pastEvents.map(event => (
                          <div 
                            key={event.id}
                            className="admin-card p-4 flex items-center justify-between opacity-70 hover:opacity-100"
                          >
                            <div className="flex items-center gap-4">
                              <div className="w-16 h-12 flex-shrink-0 overflow-hidden rounded-md border border-[#27272A]">
                                <img
                                  src={event.image_url || 'https://images.unsplash.com/photo-1704912343324-a5304d0a160e?w=200'}
                                  alt={event.title}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div>
                                <p className="font-bold text-[#A1A1AA]">{event.title}</p>
                                <p className="text-xs text-[#52525B]">
                                  {formatInTimeZone(new Date(event.date), 'America/Argentina/Buenos_Aires', 'd MMM yyyy', { locale: es })}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              {event.drive_folder_id ? (
                                <>
                                  <span className="badge-success flex items-center gap-1">
                                    <CheckCircle size={12} />
                                    Drive conectado
                                  </span>
                                  <a 
                                    href={`https://drive.google.com/drive/folders/${event.drive_folder_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="admin-admin-btn-secondary text-xs px-3 py-1.5"
                                  >
                                    <ExternalLink size={12} className="mr-1" />
                                    Abrir
                                  </a>
                                </>
                              ) : (
                                <span className="flex items-center gap-1 text-[#52525B] text-xs">
                                  <XCircle size={14} />
                                  Sin carpeta
                                </span>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenFolderModal(event)}
                                className="text-xs hover:text-[#D4AF37]"
                              >
                                <Edit size={14} className="mr-1" />
                                {event.drive_folder_id ? 'Editar' : 'Agregar'}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {events.length === 0 && (
                    <div className="admin-card p-12 text-center">
                      <Calendar size={48} className="mx-auto mb-4 text-[#27272A]" />
                      <h3 className="text-xl font-black uppercase mb-2">Sin eventos</h3>
                      <p className="text-[#A1A1AA] mb-6">
                        Crea eventos para poder asignarles carpetas de Google Drive
                      </p>
                      <Link to="/admin/eventos" className="admin-admin-btn-primary">
                        Ir a Eventos
                      </Link>
                    </div>
                  )}
                </div>

                {/* Drive Gallery Preview */}
                {driveGallery.length > 0 && (
                  <div className="mt-8">
                    <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-4">
                      Vista previa de galería ({driveGallery.length} archivos)
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-2">
                      {driveGallery.slice(0, 16).map((item) => (
                        <motion.div
                          key={item.id}
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="aspect-square relative group overflow-hidden rounded-md border border-[#27272A] bg-[#121212] hover:border-[#D4AF37]/50"
                        >
                          {item.type === 'image' ? (
                            <img
                              src={item.thumbnail_url}
                              alt={item.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-[#1A1A1A]">
                              <Video size={20} className="text-[#52525B]" />
                            </div>
                          )}
                          {item.event_title && (
                            <div className="absolute bottom-0 left-0 right-0 bg-black/80 px-2 py-1 text-[9px] truncate">
                              {item.event_title}
                            </div>
                          )}
                        </motion.div>
                      ))}
                    </div>
                    {driveGallery.length > 16 && (
                      <p className="text-center text-[#52525B] text-xs mt-4">
                        +{driveGallery.length - 16} más
                      </p>
                    )}
                  </div>
                )}
              </TabsContent>

              {/* Manual Upload Tab */}
              <TabsContent value="manual">
                <div className="flex items-center justify-between mb-6">
                  <Select value={filterEvent} onValueChange={setFilterEvent}>
                    <SelectTrigger className="w-64 bg-[#0A0A0A] border border-[#27272A] rounded-md">
                      <SelectValue placeholder="Filtrar por evento" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border border-[#27272A]">
                      <SelectItem value="all">Todos los eventos</SelectItem>
                      {events.map(event => (
                        <SelectItem key={event.id} value={event.id}>{event.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Button 
                    onClick={() => setModalOpen(true)}
                    className="admin-admin-btn-primary"
                    data-testid="add-media-btn"
                  >
                    <Plus size={18} className="mr-2" />
                    Agregar Medio
                  </Button>
                </div>

                {/* Manual Gallery Grid */}
                {loading ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="aspect-square skeleton rounded-md" />
                    ))}
                  </div>
                ) : gallery.length === 0 ? (
                  <div className="admin-card p-12 text-center">
                    <Image size={48} className="mx-auto mb-4 text-[#27272A]" />
                    <h3 className="text-xl font-black uppercase mb-2">Sin contenido manual</h3>
                    <p className="text-[#A1A1AA] mb-6">
                      Agrega fotos y videos manualmente por URL
                    </p>
                    <Button onClick={() => setModalOpen(true)} className="admin-admin-btn-primary">
                      <Plus size={18} className="mr-2" />
                      Agregar Medio
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {gallery.map((item) => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="aspect-square relative group overflow-hidden rounded-md border border-[#27272A] bg-[#121212] hover:border-[#D4AF37]/50"
                      >
                        {imageErrors[item.id] ? (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-[#1A1A1A] p-4">
                            <ImageOff size={32} className="text-[#52525B] mb-2" />
                            <span className="text-[#52525B] text-xs text-center">Imagen no disponible</span>
                          </div>
                        ) : item.media_type === 'image' ? (
                          <img
                            src={item.url}
                            alt={item.caption || 'Gallery'}
                            className="w-full h-full object-cover"
                            onError={() => handleImageError(item.id)}
                          />
                        ) : (
                          <video
                            src={item.url}
                            className="w-full h-full object-cover"
                            onError={() => handleImageError(item.id)}
                          />
                        )}
                        
                        <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(item.id)}
                            className="text-red-500 hover:text-red-400 hover:bg-red-500/20"
                          >
                            <Trash2 size={18} className="mr-2" />
                            Eliminar
                          </Button>
                          {item.caption && (
                            <p className="text-xs text-[#A1A1AA] px-4 text-center truncate max-w-full">
                              {item.caption}
                            </p>
                          )}
                        </div>

                        <div className="absolute top-2 left-2 bg-black/70 p-1.5 rounded">
                          {item.media_type === 'image' ? (
                            <Image size={14} />
                          ) : (
                            <Video size={14} />
                          )}
                        </div>

                        {imageErrors[item.id] && (
                          <div className="absolute top-2 right-2 bg-red-500/80 p-1.5 rounded">
                            <ImageOff size={14} />
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </motion.div>
        </motion.div>

      {/* Add Media Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-[#121212] border border-[#27272A] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black uppercase tracking-tight">
              Agregar Medio
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6 py-4">
            <div>
              <Label className="text-sm uppercase tracking-widest text-[#A1A1AA] mb-2 block">
                Evento
              </Label>
              <Select 
                value={formData.event_id} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, event_id: value }))}
              >
                <SelectTrigger className="input-bravo">
                  <SelectValue placeholder="Seleccionar evento" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border border-[#27272A]">
                  {events.map(event => (
                    <SelectItem key={event.id} value={event.id}>{event.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm uppercase tracking-widest text-[#A1A1AA] mb-2 block">
                Tipo
              </Label>
              <Select 
                value={formData.media_type} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, media_type: value }))}
              >
                <SelectTrigger className="input-bravo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border border-[#27272A]">
                  <SelectItem value="image">Imagen</SelectItem>
                  <SelectItem value="video">Video</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm uppercase tracking-widest text-[#A1A1AA] mb-2 block">
                URL
              </Label>
              <Input
                value={formData.url}
                onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                placeholder="https://..."
                required
                className="input-bravo"
              />
            </div>

            <div>
              <Label className="text-sm uppercase tracking-widest text-[#A1A1AA] mb-2 block">
                Descripción (opcional)
              </Label>
              <Input
                value={formData.caption}
                onChange={(e) => setFormData(prev => ({ ...prev, caption: e.target.value }))}
                placeholder="Descripción de la imagen/video"
                className="input-bravo"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting} className="admin-btn-primary">
                {submitting ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  'Agregar'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Folder ID Modal */}
      <Dialog open={folderModalOpen} onOpenChange={setFolderModalOpen}>
        <DialogContent className="bg-[#121212] border border-[#27272A] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-black uppercase tracking-tight">
              Carpeta de Google Drive
            </DialogTitle>
          </DialogHeader>
          
          {selectedEventForFolder && (
            <div className="space-y-6 py-4">
              <div className="flex items-center gap-4 p-4 bg-[#0A0A0A] border border-[#27272A]">
                <div className="w-16 h-12 flex-shrink-0 overflow-hidden border border-[#27272A]">
                  <img
                    src={selectedEventForFolder.image_url || 'https://images.unsplash.com/photo-1704912343324-a5304d0a160e?w=200'}
                    alt={selectedEventForFolder.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="font-bold">{selectedEventForFolder.title}</p>
                  <p className="text-xs text-[#52525B]">
                    {formatInTimeZone(new Date(selectedEventForFolder.date), 'America/Argentina/Buenos_Aires', 'd MMM yyyy', { locale: es })}
                  </p>
                </div>
              </div>

              <div>
                <Label className="text-sm uppercase tracking-widest text-[#A1A1AA] mb-2 block">
                  ID de Carpeta de Google Drive
                </Label>
                <Input
                  value={folderIdInput}
                  onChange={(e) => setFolderIdInput(e.target.value)}
                  placeholder="1abc123... (copia el ID de la URL de Drive)"
                  className="input-bravo font-mono"
                />
                <p className="text-[#52525B] text-xs mt-2">
                  Ejemplo: drive.google.com/drive/folders/<span className="text-blue-400">1abc123XYZ</span>
                </p>
              </div>

              <div className="p-4 bg-blue-500/10 border border-blue-500/30 text-sm">
                <p className="text-blue-400 font-bold mb-1">¿Cómo obtener el ID?</p>
                <ol className="text-blue-300/80 text-xs space-y-1 list-decimal list-inside">
                  <li>Abre Google Drive y crea o selecciona una carpeta</li>
                  <li>Haz clic derecho → "Compartir" → "Cualquiera con el enlace"</li>
                  <li>Copia el ID de la URL (después de /folders/)</li>
                </ol>
              </div>

              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setFolderModalOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleSaveFolder} 
                  disabled={savingFolder} 
                  className="admin-btn-primary"
                >
                  {savingFolder ? (
                    <>
                      <Loader2 size={18} className="mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    'Guardar'
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
        </AdminLayout>
      </SidebarProvider>
  );
};

export default GalleryManagerPage;
